import { useState } from 'react';
import { useEsriMapContext } from '../EsriMapContext';
import type Polygon from '@arcgis/core/geometry/Polygon';
import type { AssetRecord } from '../types';
import type { Viewport } from './useViewport';
import type { Selection } from './useSelection';
import type { Asset } from './AssetGrid/utils/types';

// Features #6, #26–27: toggle grid, data loading
//
// This hook manages grid state and data only.
// The AssetGrid UI component is rendered separately by the consuming app.

type UseGridOptions = {
	viewport?: Viewport;
	selection?: Selection;
};

export const useGrid = ({
	viewport: _viewport,
	selection,
}: UseGridOptions = {}) => {
	const { view } = useEsriMapContext();

	const [isGridVisible, setIsGridVisible] = useState(false);
	const [gridData, setGridData] = useState<AssetRecord[]>([]);
	const [selectedSystemType, setSelectedSystemType] = useState<string | null>(
		null,
	);
	const [selectedAssetType, setSelectedAssetType] = useState<string | null>(
		null,
	);
	const [filteredAssetIds, setFilteredAssetIds] = useState<string[]>([]);

	return {
		isGridVisible,
		gridData,
		selectedSystemType,
		selectedAssetType,
		filteredAssetIds,

		toggleGrid(): void {
			setIsGridVisible(prev => !prev);
		},

		showGrid(): void {
			setIsGridVisible(true);
		},

		hideGrid(): void {
			setIsGridVisible(false);
		},

		setSystemType(systemType: string): void {
			setSelectedSystemType(systemType);
		},

		setAssetType(assetType: string): void {
			setSelectedAssetType(assetType);
		},

		async loadGridData(
			systemType: string,
			assetType: string,
		): Promise<void> {
			// TODO: FeatureLayer.queryFeatures({ where: `systemType='${systemType}' AND assetType='${assetType}'` })
			//       map results to AssetRecord[], setGridData(records)
		},

		async refreshGridData(): Promise<void> {
			// TODO: re-run loadGridData with current systemType / assetType
		},

		filterByPolygon(polygon: Polygon): void {
			// TODO: apply polygon geometry as spatial filter on grid query
		},

		syncGridToMap(assetIds: string[]): void {
			// TODO: trigger selection on map from grid row selection
		},

		syncMapToGrid(assetIds: string[]): void {
			// TODO: highlight rows in AssetGrid that match assetIds selected on map
		},

		onGridRowSelect(selectedAssets: Asset[]): void {
			if (!selectedAssets.length) {
				selection?.clearSelection();
				return;
			}

			const ids = selectedAssets.map(a => a._id);

			if (selectedAssets.length > 1) {
				selection?.selectAssets(ids);
				const geometries = selectedAssets
					.map(a => a.geometry)
					.filter((g): g is GeoJSON.Geometry => !!g);
				if (geometries.length) {
					selection?.zoomToAssets(ids, geometries);
				}
				return;
			}

			const asset = selectedAssets[0];
			selection?.selectAsset(asset._id);

			if (!asset.geometry) {
				console.warn(
					'[useGrid] no geometry available for asset, skipping fly-to',
					asset._id,
				);
				return;
			}

			if (asset.geometry.type === 'Point') {
				const [lng, lat] = (asset.geometry as GeoJSON.Point)
					.coordinates;
				selection?.zoomToLngLat(lng, lat, 19);
			} else {
				selection?.zoomToAssets(
					[asset._id],
					[asset.geometry as GeoJSON.Geometry],
				);
			}
		},
	};
};

export type Grid = ReturnType<typeof useGrid>;
